import {
  PackageLine,
  FreightMode,
  DimensionUnit,
  WeightUnit,
  FreightCostInput,
} from '@freight-cbm/shared-types';

export interface DemoDataset {
  id: string;
  name: string;
  description: string;
  tag: string;
  mode: FreightMode;
  dimDivisor: number;
  packages: Omit<PackageLine, 'id'>[];
  costInput: Partial<FreightCostInput>;
}

/*
 * Dataset count justification (N=6):
 * The engine has three distinct freight modes (Air, Ocean LCL, Courier) with
 * different DIM weight and costing logic. It also supports metric and imperial
 * unit paths. The 6 datasets cover:
 *   1. single-pallet     — minimal/simple case (1 line, 1 package)
 *   2. air-electronics   — typical mid-complexity Air shipment (3 lines, 200 pkgs)
 *   3. courier-ecommerce — typical Courier shipment, DIM-weight-driven (4 lines, 75 pkgs)
 *   4. ocean-mixed-cargo — typical Ocean LCL with accessorials (4 lines, 45 pkgs)
 *   5. imperial-machinery — imperial unit branching path (in/lb, divisor=166)
 *   6. fcl-prep          — maximal/stress case (3 lines, 30 pallets, container rate)
 * No additional branching paths exist beyond these: all engine code paths
 * (CBM, DIM weight, chargeable weight, revenue ton, container fit, pallet fit,
 * density classification, cost calculation) are exercised by this set.
 */

export const DEMO_DATASETS: DemoDataset[] = [
  {
    id: 'air-electronics',
    name: 'Air Freight — Electronics',
    description:
      'Smartphone shipment from Shenzhen to Frankfurt. 200 cartons on a single pallet, typical for consumer electronics air cargo.',
    tag: 'Air',
    mode: FreightMode.Air,
    dimDivisor: 6000,
    packages: [
      { length: 60, width: 40, height: 35, dimUnit: DimensionUnit.Cm, weight: 12, weightUnit: WeightUnit.Kg, quantity: 50 },
      { length: 50, width: 35, height: 30, dimUnit: DimensionUnit.Cm, weight: 9.5, weightUnit: WeightUnit.Kg, quantity: 80 },
      { length: 45, width: 30, height: 25, dimUnit: DimensionUnit.Cm, weight: 7, weightUnit: WeightUnit.Kg, quantity: 70 },
    ],
    costInput: { ratePerKg: 4.2, currency: 'USD', accessorials: [] },
  },
  {
    id: 'ocean-mixed-cargo',
    name: 'Ocean LCL — Mixed Cargo',
    description:
      'Consolidated less-than-container load from Shanghai to Los Angeles. Furniture, textiles, and hardware on euro pallets.',
    tag: 'Ocean',
    mode: FreightMode.OceanLCL,
    dimDivisor: 1000,
    packages: [
      { length: 120, width: 80, height: 110, dimUnit: DimensionUnit.Cm, weight: 450, weightUnit: WeightUnit.Kg, quantity: 4 },
      { length: 120, width: 80, height: 90, dimUnit: DimensionUnit.Cm, weight: 320, weightUnit: WeightUnit.Kg, quantity: 6 },
      { length: 60, width: 40, height: 40, dimUnit: DimensionUnit.Cm, weight: 25, weightUnit: WeightUnit.Kg, quantity: 20 },
      { length: 50, width: 50, height: 80, dimUnit: DimensionUnit.Cm, weight: 18, weightUnit: WeightUnit.Kg, quantity: 15 },
    ],
    costInput: { ratePerCBM: 65, ratePerRevenueTon: 65, currency: 'USD', accessorials: [{ name: 'B/L fee', cost: 50 }, { name: 'Destination THC', cost: 120 }] },
  },
  {
    id: 'courier-ecommerce',
    name: 'Courier — E-Commerce Parcels',
    description:
      'DHL/FedEx express shipment of online orders from a UK warehouse to US customers. Multiple small parcels, DIM weight driven.',
    tag: 'Courier',
    mode: FreightMode.Courier,
    dimDivisor: 5000,
    packages: [
      { length: 40, width: 30, height: 20, dimUnit: DimensionUnit.Cm, weight: 2.5, weightUnit: WeightUnit.Kg, quantity: 12 },
      { length: 35, width: 25, height: 15, dimUnit: DimensionUnit.Cm, weight: 1.8, weightUnit: WeightUnit.Kg, quantity: 25 },
      { length: 50, width: 40, height: 10, dimUnit: DimensionUnit.Cm, weight: 3.2, weightUnit: WeightUnit.Kg, quantity: 8 },
      { length: 25, width: 20, height: 12, dimUnit: DimensionUnit.Cm, weight: 0.9, weightUnit: WeightUnit.Kg, quantity: 30 },
    ],
    costInput: { ratePerKg: 8.5, currency: 'USD', accessorials: [{ name: 'Fuel surcharge', cost: 35 }] },
  },
  {
    id: 'imperial-machinery',
    name: 'Machinery Parts (Imperial)',
    description:
      'Heavy industrial equipment parts from Chicago to Hamburg. Dimensions in inches, weight in pounds. Demonstrates imperial unit workflow.',
    tag: 'Air',
    mode: FreightMode.Air,
    dimDivisor: 166,
    packages: [
      { length: 48, width: 36, height: 24, dimUnit: DimensionUnit.In, weight: 220, weightUnit: WeightUnit.Lb, quantity: 2 },
      { length: 36, width: 30, height: 18, dimUnit: DimensionUnit.In, weight: 165, weightUnit: WeightUnit.Lb, quantity: 3 },
      { length: 24, width: 24, height: 16, dimUnit: DimensionUnit.In, weight: 95, weightUnit: WeightUnit.Lb, quantity: 4 },
    ],
    costInput: { ratePerKg: 3.8, currency: 'USD', accessorials: [{ name: 'Heavy-lift fee', cost: 150 }, { name: 'Dangerous goods screening', cost: 75 }] },
  },
  {
    id: 'fcl-prep',
    name: 'FCL Prep — Full Container Load',
    description:
      'Pre-staging calculation before booking a 40ft High Cube. Palletized consumer goods — check how many pallets fill the container.',
    tag: 'Ocean',
    mode: FreightMode.OceanLCL,
    dimDivisor: 1000,
    packages: [
      { length: 120, width: 80, height: 160, dimUnit: DimensionUnit.Cm, weight: 500, weightUnit: WeightUnit.Kg, quantity: 10 },
      { length: 120, width: 80, height: 140, dimUnit: DimensionUnit.Cm, weight: 420, weightUnit: WeightUnit.Kg, quantity: 10 },
      { length: 120, width: 80, height: 120, dimUnit: DimensionUnit.Cm, weight: 380, weightUnit: WeightUnit.Kg, quantity: 10 },
    ],
    costInput: { ratePerContainer: 2800, currency: 'USD', accessorials: [{ name: 'Origin THC', cost: 200 }, { name: 'Destination THC', cost: 250 }] },
  },
  {
    id: 'single-pallet',
    name: 'Single Pallet — Quick Quote',
    description:
      'One standard euro pallet of auto parts. Minimal input for a fast volume and weight check — ideal first demo.',
    tag: 'Air',
    mode: FreightMode.Air,
    dimDivisor: 6000,
    packages: [
      { length: 120, width: 80, height: 100, dimUnit: DimensionUnit.Cm, weight: 350, weightUnit: WeightUnit.Kg, quantity: 1 },
    ],
    costInput: { ratePerKg: 3.5, currency: 'EUR', accessorials: [] },
  },
];
